import { useState } from 'react';
import { Plus, Loader2, Building } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { cn } from '@/lib/utils';
import { extractErrorMessage } from '@/lib/error';
import { formatMoney } from '@/lib/money';
import { PageHeader } from '@/components/ui/PageHeader';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { SlideOver } from '@/components/ui/SlideOver';
import {
  useHouseAccounts,
  useCreateHouseAccount,
  useHouseAccountItems,
  usePostHouseAccountItem,
} from '@/hooks/api/useHouseAccounts';
import { useSelectedPropertyId } from '@/contexts/PropertyContext';
import {
  HouseAccountItemDirection,
  type HouseAccountWithBalance,
} from '@/services/api/frontdeskOps.types';

function fmtDate(iso: string): string {
  try {
    return format(parseISO(iso), 'MMM d, yyyy');
  } catch {
    return iso;
  }
}

// ─── Page ────────────────────────────────────────────────────────────────────

export function HouseAccountsPage() {
  const propertyId = useSelectedPropertyId();
  const [showCreate, setShowCreate] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const listQuery = useHouseAccounts({ propertyId }, !!propertyId);
  const accounts: HouseAccountWithBalance[] = listQuery.data ?? [];

  return (
    <div>
      <PageHeader
        title="House Accounts"
        description="Non-guest ledgers with manual charges and credits"
        actions={
          <Button onClick={() => setShowCreate(true)} disabled={!propertyId}>
            <Plus size={16} aria-hidden="true" />
            New Account
          </Button>
        }
      />

      {!propertyId ? (
        <EmptyState icon={Building} title="Select a property" description="Select a property to continue." />
      ) : listQuery.isLoading ? (
        <div className="flex items-center justify-center py-20">
          <Loader2 size={32} className="animate-spin text-primary-500" aria-label="Loading" />
        </div>
      ) : listQuery.isError ? (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(listQuery.error)}
        </p>
      ) : accounts.length === 0 ? (
        <EmptyState
          icon={Building}
          title="No house accounts"
          description="Create a house account to start posting line items."
          actionLabel="New Account"
          onAction={() => setShowCreate(true)}
        />
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <Th>Name</Th>
                  <Th>Description</Th>
                  <Th>Balance</Th>
                  <Th>Created</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {accounts.map((acc) => (
                  <tr
                    key={acc.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedId(acc.id)}
                  >
                    <td className="px-4 py-3 text-sm text-gray-900 font-medium">{acc.name}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-xs truncate">{acc.description ?? '--'}</td>
                    <td className="px-4 py-3 text-sm font-bold text-gray-900">{formatMoney(acc.balance)}</td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{fmtDate(acc.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Create slide-over */}
      <SlideOver isOpen={showCreate} onClose={() => setShowCreate(false)} title="New House Account">
        <CreateHouseAccountForm propertyId={propertyId} onClose={() => setShowCreate(false)} />
      </SlideOver>

      {/* Detail slide-over */}
      <SlideOver
        isOpen={selectedId !== null}
        onClose={() => setSelectedId(null)}
        title="House Account"
        width="max-w-xl"
      >
        {selectedId !== null && <HouseAccountDetail id={selectedId} />}
      </SlideOver>
    </div>
  );
}

// ─── Create form ─────────────────────────────────────────────────────────────

function CreateHouseAccountForm({
  propertyId,
  onClose,
}: {
  propertyId: string;
  onClose: () => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const createMutation = useCreateHouseAccount();
  const isValid = !!propertyId && name.trim().length > 0;

  function clearError() {
    if (createMutation.isError) createMutation.reset();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    createMutation.mutate(
      {
        propertyId,
        name: name.trim(),
        ...(description.trim() ? { description: description.trim() } : {}),
      },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <label htmlFor="ha-name" className="block text-sm font-medium text-gray-700 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          id="ha-name"
          type="text"
          required
          value={name}
          onChange={(e) => { setName(e.target.value); clearError(); }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div>
        <label htmlFor="ha-desc" className="block text-sm font-medium text-gray-700 mb-1">Description</label>
        <textarea
          id="ha-desc"
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      {createMutation.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(createMutation.error)}
        </p>
      )}
      <div className="flex justify-end gap-3 pt-4 border-t border-gray-100">
        <Button type="button" variant="secondary" onClick={onClose}>Cancel</Button>
        <Button type="submit" isLoading={createMutation.isPending} disabled={!isValid}>Create Account</Button>
      </div>
    </form>
  );
}

// ─── Detail view ─────────────────────────────────────────────────────────────

function HouseAccountDetail({ id }: { id: string }) {
  const { data, isLoading } = useHouseAccountItems(id);
  const [showPost, setShowPost] = useState(false);

  if (isLoading || !data) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 size={24} className="animate-spin text-primary-500" aria-label="Loading" />
      </div>
    );
  }

  const { account, items } = data;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="space-y-2">
        <h3 className="text-lg font-semibold text-gray-900">{account.name}</h3>
        {account.description && <p className="text-sm text-gray-500">{account.description}</p>}
        <div className="flex items-baseline gap-2">
          <span className="text-sm text-gray-500">Balance</span>
          <span className="text-xl font-bold text-gray-900">{formatMoney(account.balance)}</span>
        </div>
      </div>

      {/* Post action */}
      <div className="flex items-center gap-3 pt-2 border-t border-gray-100">
        <Button size="sm" onClick={() => setShowPost(true)}>
          <Plus size={14} aria-hidden="true" />
          Post Line
        </Button>
      </div>

      {showPost && (
        <PostLineForm accountId={id} onClose={() => setShowPost(false)} />
      )}

      {/* Line items */}
      <div className="pt-2">
        <h4 className="text-sm font-semibold text-gray-700 mb-3">Transactions</h4>
        {items.length === 0 ? (
          <p className="text-sm text-gray-400">No transactions yet.</p>
        ) : (
          <div className="overflow-x-auto border border-gray-100 rounded-lg">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <ThSm>Date</ThSm>
                  <ThSm>Type</ThSm>
                  <ThSm>Description</ThSm>
                  <ThSm>Amount</ThSm>
                  <ThSm>Posted By</ThSm>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {items.map((it) => {
                  const isCredit = it.direction === HouseAccountItemDirection.CREDIT;
                  return (
                    <tr key={it.id}>
                      <td className="px-3 py-2 text-sm text-gray-700 whitespace-nowrap">{fmtDate(it.entryDate)}</td>
                      <td className="px-3 py-2 text-sm capitalize text-gray-700">{it.direction}</td>
                      <td className="px-3 py-2 text-sm text-gray-700">{it.description}</td>
                      <td className={cn('px-3 py-2 text-sm font-medium', isCredit ? 'text-green-700' : 'text-gray-600')}>
                        {isCredit ? '-' : '+'}{formatMoney(it.amount)}
                      </td>
                      <td className="px-3 py-2 text-sm text-gray-500">{it.postedByName ?? '--'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Post line form ──────────────────────────────────────────────────────────

function PostLineForm({
  accountId,
  onClose,
}: {
  accountId: string;
  onClose: () => void;
}) {
  const [direction, setDirection] = useState<HouseAccountItemDirection>(HouseAccountItemDirection.CHARGE);
  const [amount, setAmount] = useState('');
  const [description, setDescription] = useState('');
  const [entryDate, setEntryDate] = useState(() => format(new Date(), 'yyyy-MM-dd'));

  const postMutation = usePostHouseAccountItem();
  const parsedAmount = parseFloat(amount);
  const isValid =
    Number.isFinite(parsedAmount) && parsedAmount >= 0.01 && description.trim().length > 0;

  function clearError() {
    if (postMutation.isError) postMutation.reset();
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!isValid) return;
    postMutation.mutate(
      {
        id: accountId,
        payload: {
          direction,
          amount: parsedAmount,
          description: description.trim(),
          entryDate,
        },
      },
      { onSuccess: () => onClose() },
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4 bg-gray-50 border border-gray-200 rounded-lg p-4">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label htmlFor="hl-dir" className="block text-sm font-medium text-gray-700 mb-1">
            Direction <span className="text-red-500">*</span>
          </label>
          <select
            id="hl-dir"
            value={direction}
            onChange={(e) => setDirection(e.target.value as HouseAccountItemDirection)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            <option value={HouseAccountItemDirection.CHARGE}>Charge</option>
            <option value={HouseAccountItemDirection.CREDIT}>Credit</option>
          </select>
        </div>
        <div>
          <label htmlFor="hl-date" className="block text-sm font-medium text-gray-700 mb-1">Date</label>
          <input
            id="hl-date"
            type="date"
            value={entryDate}
            onChange={(e) => setEntryDate(e.target.value)}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>
      </div>
      <div>
        <label htmlFor="hl-amount" className="block text-sm font-medium text-gray-700 mb-1">
          Amount <span className="text-red-500">*</span>
        </label>
        <input
          id="hl-amount"
          type="number"
          step="0.01"
          min="0.01"
          required
          value={amount}
          onChange={(e) => { setAmount(e.target.value); clearError(); }}
          placeholder="0.00"
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      <div>
        <label htmlFor="hl-desc" className="block text-sm font-medium text-gray-700 mb-1">
          Description <span className="text-red-500">*</span>
        </label>
        <input
          id="hl-desc"
          type="text"
          required
          value={description}
          onChange={(e) => { setDescription(e.target.value); clearError(); }}
          className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
      </div>
      {postMutation.isError && (
        <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" role="alert">
          {extractErrorMessage(postMutation.error)}
        </p>
      )}
      <div className="flex justify-end gap-3">
        <Button type="button" variant="secondary" size="sm" onClick={onClose}>Cancel</Button>
        <Button type="submit" size="sm" isLoading={postMutation.isPending} disabled={!isValid}>Post</Button>
      </div>
    </form>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function Th({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}

function ThSm({ children }: { children: React.ReactNode }) {
  return (
    <th scope="col" className="px-3 py-2 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider bg-gray-50">
      {children}
    </th>
  );
}
